export type Severity = 'red' | 'orange' | null;

export type FieldConfig = {
  label: string;
  treatsEmptyAsUnchecked?: boolean;
  emptySeverity?: Severity;
  invalidSeverity?: Severity;
  validator?: (
    value: string,
    el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  ) => boolean;
};

function identifyVinNumber(vin: string): boolean {
  if (!vin) return false;
  var cleanedVin = vin.trim().toUpperCase();
  cleanedVin = cleanedVin.replace(/O/g,'0').replace(/o/g,'0');
  cleanedVin = cleanedVin.replace(/Q/g,'0').replace(/q/g,'0');
  cleanedVin = cleanedVin.replace(/I/g,'1').replace(/i/g,'1');
  if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(cleanedVin)) return false;
  return true;
}

function isDateWithinCampaign(dateString: string): boolean {
  if (!dateString) return false;
  try {
    const campaignStart = process.env.SVELTE_APP_APP_START_DATE;
    const campaignEnd = process.env.SVELTE_APP_APP_END_DATE;

    if (!campaignStart || !campaignEnd) return true;

    const inputDate = new Date(dateString);
    const startDate = new Date(campaignStart);
    const endDate = new Date(campaignEnd);

    endDate.setHours(23, 59, 59, 999);

    return inputDate >= startDate && inputDate <= endDate;
  } catch (error) {
    console.error('Error validating campaign date:', error);
    return false;
  }
}

export const personalFields: FieldConfig[] = [
  { label: 'name', emptySeverity: 'red' },
  { label: 'email', emptySeverity: 'red' },
  { label: 'iban', emptySeverity: 'red' },
  { label: 'ibanVerified', treatsEmptyAsUnchecked: true, emptySeverity: 'red' },
  { label: 'company', emptySeverity: 'red' },
  { label: 'ibanAccountHolder', emptySeverity: 'red' },
  {
    label: 'address',
    emptySeverity: 'red',
  },
];

export const purchaseFields: FieldConfig[] = [
  {
    label: 'purchaseDate',
    emptySeverity: 'red',
    invalidSeverity: 'orange',
    validator: (value: string) => {
      if (!value || value.trim() === '') return false;
      return isDateWithinCampaign(value);
    },
  },
  {
    label: 'storage',
    emptySeverity: 'orange',
  },
  {
    label: 'switch',
    emptySeverity: 'red',
  },
  {
    label: 'dealerships',
    emptySeverity: 'red',
  },
  {
    label: 'partsId',
    emptySeverity: 'red',
  },
  {
    label: 'partsCount',
    emptySeverity: 'red',
    invalidSeverity: 'orange',
    validator: (value: string) => {
      const trimmedValue = (value ?? '').toString().trim();
      if (trimmedValue === '') return true;
      const numericValue = Number(trimmedValue);
      return !isNaN(numericValue) && numericValue === 4;
    },
  },
  {
    label: 'partsIdentified',
    emptySeverity: 'red',
  },
  {
    label: 'vehicleVersion',
    emptySeverity: 'orange',
  },
  {
    label: 'vehicleVin',
    emptySeverity: 'red',
    invalidSeverity: 'orange',
    validator: identifyVinNumber,
  },
];

const STYLES = {
  red: {
    border: '1px solid #f00',
    boxShadow: '0 0 0 2px rgba(255,0,0,0.12)',
  },
  orange: {
    border: '1px solid #f59e0b',
    boxShadow: '0 0 0 2px rgba(245,158,11,0.12)',
  },
  transition: 'border 150ms ease, box-shadow 150ms ease',
};

const CLEANUPS: Map<Element, () => void> = new Map();

function findInputByLabel(
  root: ParentNode,
  label: string,
): HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null {
  const attr = `[inputlabel="${label}"]`;

  const direct =
    (root.querySelector(`input${attr}`) as HTMLInputElement | null) ||
    (root.querySelector(`textarea${attr}`) as HTMLTextAreaElement | null) ||
    (root.querySelector(`select${attr}`) as HTMLSelectElement | null);
  if (direct) return direct;

  const wrapper = root.querySelector(attr) as Element | null;
  if (wrapper) {
    return (
      (wrapper.querySelector('input') as HTMLInputElement | null) ||
      (wrapper.querySelector('textarea') as HTMLTextAreaElement | null) ||
      (wrapper.querySelector('select') as HTMLSelectElement | null) ||
      null
    );
  }

  return null;
}

function getControl(el: Element | null): HTMLElement | null {
  if (!el) return null;
  if (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  ) {
    return el as HTMLElement;
  }
  if ((el as Element).querySelector) {
    return (el as Element).querySelector(
      'input,textarea,select',
    ) as HTMLElement | null;
  }
  return null;
}

function applySeverityStyle(el: Element | null, sev: Severity | null) {
  if (!el) return;

  const control = getControl(el);
  if (!control) return;

  if (sev === 'red') {
    control.style.setProperty('border', STYLES.red.border, 'important');
    control.style.setProperty('box-shadow', STYLES.red.boxShadow, 'important');
    control.style.setProperty('outline', 'none', 'important');
    control.style.setProperty('transition', STYLES.transition);
    control.setAttribute('aria-invalid', 'true');
  } else if (sev === 'orange') {
    control.style.setProperty('border', STYLES.orange.border, 'important');
    control.style.setProperty(
      'box-shadow',
      STYLES.orange.boxShadow,
      'important',
    );
    control.style.setProperty('outline', 'none', 'important');
    control.style.setProperty('transition', STYLES.transition);
  } else {
    control.style.removeProperty('border');
    control.style.removeProperty('box-shadow');
    control.style.removeProperty('outline');
    control.style.removeProperty('transition');
    control.removeAttribute('aria-invalid');
  }
}

function clearStyle(el: Element | null) {
  applySeverityStyle(el, null);
}

function isEmptyValue(
  input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  treatUncheckedAsEmpty = false,
) {
  try {
    if (input instanceof HTMLInputElement && input.type === 'checkbox') {
      return treatUncheckedAsEmpty ? !input.checked : false;
    }
    return (input.value ?? '').toString().trim() === '';
  } catch {
    return false;
  }
}

// Enhanced function to handle container fields with real-time monitoring
function handleContainerField(
  root: ParentNode,
  cfg: FieldConfig,
): Element | null {
  // Find container by class name pattern
  const containerClass = `fld-${cfg.label}`;
  const container = root.querySelector(
    `.${containerClass}`,
  ) as HTMLElement | null;

  if (!container) return null;

  // Don't re-attach
  if ((container as any).__mbd_attached) return container;
  (container as any).__mbd_attached = true;

  const updateContainerState = () => {
    let isEmpty = true;

    if (cfg.label === 'dealerships') {
      // For dealerships - check the placeholder div content
      const placeholderDiv = container.querySelector(
        '.input[placeholder="Please Select"]',
      ) as HTMLElement;
      if (placeholderDiv) {
        const hasContent =
          placeholderDiv.textContent &&
          placeholderDiv.textContent.trim() !== '' &&
          placeholderDiv.textContent !== 'Please Select';

        isEmpty = !hasContent;

        if (isEmpty && cfg.emptySeverity) {
          placeholderDiv.style.setProperty(
            'border',
            STYLES.red.border,
            'important',
          );
          placeholderDiv.style.setProperty(
            'box-shadow',
            STYLES.red.boxShadow,
            'important',
          );
          placeholderDiv.style.setProperty('outline', 'none', 'important');
          placeholderDiv.style.setProperty('transition', STYLES.transition);
        } else {
          placeholderDiv.style.removeProperty('border');
          placeholderDiv.style.removeProperty('box-shadow');
          placeholderDiv.style.removeProperty('outline');
          placeholderDiv.style.removeProperty('transition');
        }
      }
    } else if (cfg.label === 'address') {
      // For address - look for the display div with border and span inside
      const addressDisplayDiv = container.querySelector(
        '.w-full.py-2.px-4.border.rounded-sm.cursor-pointer',
      ) as HTMLElement;

      if (addressDisplayDiv) {
        const addressSpan = addressDisplayDiv.querySelector(
          'span',
        ) as HTMLElement;
        const hasContent =
          addressSpan &&
          addressSpan.textContent &&
          addressSpan.textContent.trim() !== '';

        isEmpty = !hasContent;

        if (isEmpty && cfg.emptySeverity) {
          addressDisplayDiv.style.setProperty(
            'border',
            STYLES.red.border,
            'important',
          );
          addressDisplayDiv.style.setProperty(
            'box-shadow',
            STYLES.red.boxShadow,
            'important',
          );
          addressDisplayDiv.style.setProperty('outline', 'none', 'important');
          addressDisplayDiv.style.setProperty('transition', STYLES.transition);
        } else {
          addressDisplayDiv.style.removeProperty('border');
          addressDisplayDiv.style.removeProperty('box-shadow');
          addressDisplayDiv.style.removeProperty('outline');
          addressDisplayDiv.style.removeProperty('transition');
        }
      } else {
        // Fallback: try to find any element that might contain the address text
        const possibleAddressElements = container.querySelectorAll('div, span');
        let foundContent = false;

        for (const element of Array.from(possibleAddressElements)) {
          if (element.textContent && element.textContent.trim() !== '') {
            foundContent = true;
            break;
          }
        }

        isEmpty = !foundContent;

        // Apply to the container itself if we can't find the specific element
        if (isEmpty && cfg.emptySeverity) {
          container.style.setProperty('border', STYLES.red.border, 'important');
          container.style.setProperty(
            'box-shadow',
            STYLES.red.boxShadow,
            'important',
          );
        } else {
          container.style.removeProperty('border');
          container.style.removeProperty('box-shadow');
        }
      }
    }
  };

  // Set up comprehensive monitoring for real-time updates
  const eventHandlers: (() => void)[] = [];
  const observers: MutationObserver[] = [];

  if (cfg.label === 'dealerships') {
    const placeholderDiv = container.querySelector(
      '.input[placeholder="Please Select"]',
    ) as HTMLElement;
    if (placeholderDiv) {
      // MutationObserver for content changes
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        for (const mutation of mutations) {
          if (
            mutation.type === 'childList' ||
            mutation.type === 'characterData'
          ) {
            shouldUpdate = true;
            break;
          }
        }
        if (shouldUpdate) {
          setTimeout(updateContainerState, 10);
        }
      });

      observer.observe(placeholderDiv, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      observers.push(observer);

      // Also observe the container for any structural changes
      const containerObserver = new MutationObserver((mutations) => {
        updateContainerState();
      });

      containerObserver.observe(container, {
        childList: true,
        subtree: true,
      });
      observers.push(containerObserver);

      // Click and focus events
      const onInteraction = () => {
        setTimeout(updateContainerState, 50);
      };

      placeholderDiv.addEventListener('click', onInteraction);
      placeholderDiv.addEventListener('focus', onInteraction);
      placeholderDiv.addEventListener('blur', onInteraction);

      eventHandlers.push(() => {
        placeholderDiv.removeEventListener('click', onInteraction);
        placeholderDiv.removeEventListener('focus', onInteraction);
        placeholderDiv.removeEventListener('blur', onInteraction);
      });
    }
  } else if (cfg.label === 'address') {
    // For address field - observe the entire container for any changes
    const addressObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      for (const mutation of mutations) {
        if (
          mutation.type === 'childList' ||
          mutation.type === 'characterData'
        ) {
          shouldUpdate = true;
          break;
        }
      }
      if (shouldUpdate) {
        setTimeout(updateContainerState, 10);
      }
    });

    addressObserver.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    observers.push(addressObserver);

    // Also observe the specific address display div if it exists
    const addressDisplayDiv = container.querySelector(
      '.w-full.py-2.px-4.border.rounded-sm.cursor-pointer',
    ) as HTMLElement;
    if (addressDisplayDiv) {
      const displayObserver = new MutationObserver((mutations) => {
        updateContainerState();
      });

      displayObserver.observe(addressDisplayDiv, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      observers.push(displayObserver);

      // Click events on the address display
      const onAddressClick = () => {
        setTimeout(updateContainerState, 50);
      };

      addressDisplayDiv.addEventListener('click', onAddressClick);
      eventHandlers.push(() => {
        addressDisplayDiv.removeEventListener('click', onAddressClick);
      });
    }

    // Fallback: observe any span changes in the container
    const addressSpans = container.querySelectorAll('span');
    addressSpans.forEach((span) => {
      const spanObserver = new MutationObserver((mutations) => {
        updateContainerState();
      });

      spanObserver.observe(span, {
        characterData: true,
        childList: true,
        subtree: true,
      });
      observers.push(spanObserver);
    });
  }

  // Periodic check as fallback (for cases where events might be missed)
  const intervalId = setInterval(updateContainerState, 1000);

  const cleanup = () => {
    // Clear interval
    clearInterval(intervalId);

    // Disconnect all observers
    observers.forEach((observer) => {
      try {
        observer.disconnect();
      } catch {}
    });

    // Remove all event listeners
    eventHandlers.forEach((cleanupFn) => {
      try {
        cleanupFn();
      } catch {}
    });

    // Clear styles
    if (cfg.label === 'dealerships') {
      const placeholderDiv = container.querySelector(
        '.input[placeholder="Please Select"]',
      ) as HTMLElement;
      if (placeholderDiv) {
        placeholderDiv.style.removeProperty('border');
        placeholderDiv.style.removeProperty('box-shadow');
        placeholderDiv.style.removeProperty('outline');
        placeholderDiv.style.removeProperty('transition');
      }
    } else if (cfg.label === 'address') {
      const addressDisplayDiv = container.querySelector(
        '.w-full.py-2.px-4.border.rounded-sm.cursor-pointer',
      ) as HTMLElement;
      if (addressDisplayDiv) {
        addressDisplayDiv.style.removeProperty('border');
        addressDisplayDiv.style.removeProperty('box-shadow');
        addressDisplayDiv.style.removeProperty('outline');
        addressDisplayDiv.style.removeProperty('transition');
      }
      // Also clear from container as fallback
      container.style.removeProperty('border');
      container.style.removeProperty('box-shadow');
    }

    (container as any).__mbd_attached = false;
  };

  CLEANUPS.set(container, cleanup);

  // Initial check
  setTimeout(updateContainerState, 100);

  return container;
}

function attachBehaviorForField(root: ParentNode, cfg: FieldConfig) {
  if (cfg.label === 'dealerships' || cfg.label === 'address')
    return handleContainerField(root, cfg);

  const input = findInputByLabel(root, cfg.label);
  if (!input) return null;

  try {
    if ((input as HTMLInputElement).type === 'hidden') return null;
    if ((input as HTMLInputElement).disabled) return null;
    if ((input as HTMLInputElement).readOnly) return null;
  } catch {}

  if ((input as any).__mbd_attached) return input;
  (input as any).__mbd_attached = true;

  const updateState = (opts: { focused?: boolean } = {}) => {
    const focused = opts.focused ?? document.activeElement === input;
    const empty = isEmptyValue(input as any, !!cfg.treatsEmptyAsUnchecked);

    if (empty) {
      if (focused && cfg.emptySeverity)
        applySeverityStyle(input, cfg.emptySeverity);
      else clearStyle(input);
      return;
    }

    if (cfg.validator) {
      const valid = cfg.validator((input as any).value ?? '', input as any);
      if (!valid && cfg.invalidSeverity) {
        applySeverityStyle(input, cfg.invalidSeverity);
        return;
      }
    }

    clearStyle(input);
  };

  const onFocus = () => updateState({ focused: true });
  const onBlur = () => updateState({ focused: false });
  const onInput = () =>
    updateState({ focused: document.activeElement === input });
  const onChange = () =>
    updateState({ focused: document.activeElement === input });

  input.addEventListener('focus', onFocus);
  input.addEventListener('blur', onBlur);
  input.addEventListener('input', onInput);
  input.addEventListener('change', onChange);

  const cleanup = () => {
    try {
      input.removeEventListener('focus', onFocus);
      input.removeEventListener('blur', onBlur);
      input.removeEventListener('input', onInput);
      input.removeEventListener('change', onChange);
    } catch {}
    clearStyle(input);
    (input as any).__mbd_attached = false;
  };
  CLEANUPS.set(input, cleanup);

  updateState({ focused: document.activeElement === input });

  return input;
}

export type ObserveOptions = {
  fields?: FieldConfig[];
  debounceMs?: number;
};

export function observeAndAttachValidation(
  mountEl: HTMLElement,
  options: ObserveOptions = {},
) {
  if (options && options?.fields?.length === 0) return;
  const fields: FieldConfig[] = options.fields || [];
  const debounceMs: number = options.debounceMs ?? 25;

  fields.forEach((f) => attachBehaviorForField(mountEl, f));

  let debounceId: number | null = null;
  const obs = new MutationObserver((mutations: MutationRecord[]) => {
    let added = false;
    let removed = false;
    for (const m of mutations) {
      if (m.addedNodes && m.addedNodes.length) added = true;
      if (m.removedNodes && m.removedNodes.length) removed = true;
    }

    if (removed) {
      for (const m of mutations) {
        for (const node of Array.from(m.removedNodes)) {
          if (!(node instanceof Element)) continue;
          for (const f of fields) {
            const sel = `input[inputlabel="${f.label}"], textarea[inputlabel="${f.label}"], select[inputlabel="${f.label}"]`;
            const containerSel = `.fld-${f.label}`;

            if ((node as Element).matches && (node as Element).matches(sel)) {
              if (CLEANUPS.has(node as Element)) {
                CLEANUPS.get(node as Element)!();
                CLEANUPS.delete(node as Element);
              }
            }
            if (
              (node as Element).matches &&
              (node as Element).matches(containerSel)
            ) {
              if (CLEANUPS.has(node as Element)) {
                CLEANUPS.get(node as Element)!();
                CLEANUPS.delete(node as Element);
              }
            }
            const descendant = (node as Element).querySelector(sel);
            if (descendant && CLEANUPS.has(descendant)) {
              CLEANUPS.get(descendant)!();
              CLEANUPS.delete(descendant);
            }
            const containerDescendant = (node as Element).querySelector(
              containerSel,
            );
            if (containerDescendant && CLEANUPS.has(containerDescendant)) {
              CLEANUPS.get(containerDescendant)!();
              CLEANUPS.delete(containerDescendant);
            }
          }
        }
      }
    }

    if (!added) return;
    if (debounceId) window.clearTimeout(debounceId);
    debounceId = window.setTimeout(() => {
      fields.forEach((f) => attachBehaviorForField(mountEl, f));
      debounceId = null;
    }, debounceMs);
  });

  obs.observe(mountEl, { childList: true, subtree: true });

  const stop = () => {
    try {
      obs.disconnect();
    } catch {}
    for (const [el, fn] of CLEANUPS.entries()) {
      try {
        fn();
      } catch {}
    }
    CLEANUPS.clear();
  };

  return { stop, observer: obs };
}
